import { z } from 'zod';

export const SUPABASE_ERROR_CODES = {
  PROJECT_LIMIT_REACHED: 'PROJECT_LIMIT_REACHED',
  NEED_EXTRA_CONFIRMATION_TO_CREATE_DATABASE: 'NEED_EXTRA_CONFIRMATION_TO_CREATE_DATABASE',
  PROJECT_NAME_ALREADY_EXISTS: 'PROJECT_NAME_ALREADY_EXISTS',
};

export interface SupabaseOrganization {
  id: string;
  name: string;
  plan: 'free' | 'pro' | 'team' | 'enterprise';
}
export interface SupabaseProject {
  id: string;
  organization_id: string;
  name: string;
  region: SupabaseRegion;
  status: SupabaseProjectStatus;
}

export interface SupabaseOrganizationWithProjects extends SupabaseOrganization {
  projects: SupabaseProject[];
}

export interface SupabaseOrganizationInfo {
  id: string;
  plan: 'free' | 'pro' | 'team' | 'enterprise';
}

type ProjectApiKey = {
  name: 'anon' | 'service_role';
  api_key: string;
};

export type SupabaseProjectStatus =
  | 'ACTIVE_HEALTHY'
  | 'ACTIVE_UNHEALTHY'
  | 'COMING_UP'
  | 'GOING_DOWN'
  | 'INACTIVE'
  | 'INIT_FAILED'
  | 'REMOVED'
  | 'RESTARTING'
  | 'UNKNOWN'
  | 'UPGRADING'
  | 'PAUSING'
  | 'RESTORING'
  | 'RESTORE_FAILED'
  | 'PAUSE_FAILED'
  | 'RESIZING';

export type SupabaseBranchStatus =
  | 'CREATING_PROJECT'
  | 'RUNNING_MIGRATIONS'
  | 'MIGRATIONS_PASSED'
  | 'MIGRATIONS_FAILED'
  | 'FUNCTIONS_DEPLOYED'
  | 'FUNCTIONS_FAILED';

export type ProjectApiKeys = Array<ProjectApiKey>;

export interface SupabaseIntegration {
  apiKey: string;
  project: SupabaseProject;
  isBoltNative?: boolean;
}

export const supabaseRegionSchema = z.union([
  z.literal('us-east-1'),
  z.literal('us-east-2'),
  z.literal('us-west-1'),
  z.literal('ca-central-1'),
  z.literal('eu-west-1'),
  z.literal('eu-west-2'),
  z.literal('eu-west-3'),
  z.literal('eu-central-1'),
  z.literal('eu-central-2'),
  z.literal('eu-north-1'),
  z.literal('ap-south-1'),
  z.literal('ap-southeast-1'),
  z.literal('ap-northeast-1'),
  z.literal('ap-northeast-2'),
  z.literal('ap-southeast-2'),
  z.literal('sa-east-1'),
]);

export type SupabaseRegion = z.infer<typeof supabaseRegionSchema>;

export type SupabaseInstanceSize =
  | 'pico'
  | 'nano'
  | 'micro'
  | 'small'
  | 'medium'
  | 'large'
  | 'xlarge'
  | '2xlarge'
  | '4xlarge'
  | '8xlarge'
  | '12xlarge'
  | '16xlarge'
  | '24xlarge'
  | '24xlarge_optimized_memory'
  | '24xlarge_optimized_cpu'
  | '24xlarge_high_memory'
  | '48xlarge'
  | '48xlarge_optimized_memory'
  | '48xlarge_optimized_cpu'
  | '48xlarge_high_memory';

export type SupabasePostgresEngine = '15' | '17' | '17-oriole';

export type SupabaseReleaseChannel = 'internal' | 'alpha' | 'beta' | 'ga' | 'withdrawn' | 'preview';
